import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';

import { LoadingIndicator } from '@dojo/widgets/loading-indicator';

import { createNlsMiddleware, documentapi, userstate, lccstate } from 'lcc-state';
import { ParentTree, PostTestResults } from '@holmescorp/lcc-state/interfaces';
import { publish } from '@holmescorp/pub-sub';

import css from '../../css/progress-view.m.css';
import { MicroCardButton, svc, ScoreChart, getFinalTestScore, Hero, ScoreToast } from '../index';

interface ScoreHistory {
	score: string;
}

export interface FinalTestICache {
	posttest_results: ScoreHistory[];
}

const factory = create({
	icache: createICacheMiddleware<FinalTestICache>(),
	nls: createNlsMiddleware('ditabook'),
	theme,
	userstate,
	lccstate,
	documentapi
});

export const FinalTestPanel = factory(({ middleware: { icache, theme, userstate, nls, lccstate } }) => {
	const {
		messages: {
			practice_type = 'practice',
			card_subtitle_quiz_start = '[ditabook] card_subtitle_quiz_start',
			assess_progress_threshold_high = '80',
			use_progress_chart_color = 'false'
		}
	} = nls();

	const alertMessages = {
		none: "You haven't taken the final test yet.",
		low: "We've highlighted this test as a priority to revisit.",
		mid: "We've highlighted this test as a priority to revisit.",
		high: 'You passed the final test.'
	};

	const { get: uget, path: upath } = userstate;
	const {
		portal_site: site,
		portal_userid: userid,
		product: product_id,
		course_session,
		course_product
	} = window.hcstatic;
	const { title: product_title = '' } = course_product || {};
	const session_id = course_session && course_session.pk;

	const themeCss = theme.classes(css);

	const passingScore = parseInt(assess_progress_threshold_high) || 80;
	const useProgressChartColor = use_progress_chart_color === 'true';

	const postTestResults = icache.getOrSet('posttest_results', async () => {
		const member_id = userid || '';
		const data = await svc.attempts_quizzes({
			quiz_type: 'practice',
			member_id,
			product_id,
			session_id,
			site
		});
		return data
			.filter((result) => result.kind === practice_type)
			.flatMap((result) => {
				return result.attempts.map((attempt) => {
					return {
						score: attempt.score
					};
				});
			});
	});

	const get_tree = (practice_type: string): ParentTree => {
		const practiceResults: ParentTree = uget(upath('practice'));
		const posttestResults: PostTestResults = uget(upath('posttest'));

		switch (practice_type) {
			case 'practice':
				return practiceResults;

			case 'posttest':
				return posttestResults.tree;
		}
		return {};
	};

	const tree = get_tree(practice_type);
	const ditaroots: string[] = lccstate.get(lccstate.path('ditaroots'));
	const fullPosttest: PostTestResults = uget(upath('posttest'));

	const { score, hasPosttest } = getFinalTestScore(tree, ditaroots, practice_type, fullPosttest);

	const getHeroContent = (score: number, scoreHistory: ScoreHistory[]) => {
		const hasPassed = score >= passingScore;

		const bestScore = scoreHistory.reduce((acc, curr) => {
			return Math.max(acc, parseFloat(curr.score));
		}, 0);

		return w(Hero, { additionalClasses: [themeCss.hero] }, [
			v('img', {
				src: `${window.hcstatic.portal_cdn}/js/images/${window.hcstatic.portal_site}/badge.svg`,
				classes: [themeCss.badge]
			}),
			v('div', { classes: [themeCss.center], xqa: 'progress-view-hero-center' }, [
				v('h3', { classes: [themeCss.label] }, [product_title]),
				w(ScoreToast, { score, messages: alertMessages, useMidScore: false }),
				w(MicroCardButton, {
					labelText: card_subtitle_quiz_start,
					showIcon: true,
					darkMode: !hasPassed,
					disabled: hasPassed,
					xqa: 'progress-view-test-button',
					additionalClasses: [themeCss.quizButton],
					onClick: () => {
						publish('site/final-test');
					},
					onDown: () => {
						publish('site/final-test');
					}
				})
			]),

			hasPosttest
				? v('div', { classes: [themeCss.trailing], xqa: 'progress-view-score-chart' }, [
						w(ScoreChart, {
							score,
							bestScore,
							useBestScoreIcon: false,
							useInsideLabel: true,
							insideLabelText: 'Test Score',
							useScoreColor: useProgressChartColor,
							additionalClasses: [themeCss.small]
						})
				  ])
				: v('div', { xqa: 'progress-view-no-score-chart' }, [])
		]);
	};
	return postTestResults
		? getHeroContent(score, postTestResults)
		: w(LoadingIndicator, { active: true, type: 'circular-medium' });
});

export default FinalTestPanel;
